export const INTENT_CONTRACT = 'strategy-intent-v1';
export const CAPABILITY_CATALOG_VERSION = 'strategy-intent-capabilities-v1';
export const GUIDED_DRAFT_CONTRACT = 'guided-strategy-browser-draft-v1';
export const STRATEGY_NAME_MINIMUM_LENGTH = 3;
export const STRATEGY_NAME_MAXIMUM_LENGTH = 100;

export const CONDITION_PRESENTATION = {
  consecutive_falling_closes: { family: 'falling', label: 'The price has been falling', parameter: 'consecutive_sessions' },
  consecutive_rising_closes: { family: 'rising', label: 'The price has been rising', parameter: 'consecutive_sessions' },
  close_above_sma: { family: 'sma', label: 'The price is above its recent average', parameter: 'indicator_window' },
  close_below_sma: { family: 'sma', label: 'The price is below its recent average', parameter: 'indicator_window' },
  rsi_at_or_below: { family: 'rsi', label: 'RSI is at or below', parameter: 'rsi_threshold' },
  rsi_at_or_above: { family: 'rsi', label: 'RSI is at or above', parameter: 'rsi_threshold' },
  macd_positive: { family: 'macd', label: 'MACD is above zero', parameter: null },
  macd_negative: { family: 'macd', label: 'MACD is below zero', parameter: null },
  close_above_rolling_high: { family: 'range', label: 'The price is above the prior trading-range high', parameter: 'indicator_window' },
  close_below_rolling_low: { family: 'range', label: 'The price is below the prior trading-range low', parameter: 'indicator_window' },
};

export const conditionFromCapability = (capabilityId, value = null) => {
  const presentation = CONDITION_PRESENTATION[capabilityId];
  if (!presentation) return null;
  if (presentation.parameter === 'rsi_threshold') {
    return { capability_id: capabilityId, integer_parameter: null, decimal_parameter: String(value) };
  }
  if (presentation.parameter) {
    return { capability_id: capabilityId, integer_parameter: Number(value), decimal_parameter: null };
  }
  return { capability_id: capabilityId, integer_parameter: null, decimal_parameter: null };
};

export const numberWithinCatalogBound = (value, bound) => {
  if (value === '' || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isFinite(number) && Array.isArray(bound) && number >= Number(bound[0]) && number <= Number(bound[1]);
};

export const integerWithinCatalogBound = (value, bound) => (
  numberWithinCatalogBound(value, bound) && Number.isInteger(Number(value))
);

export const guidedStrategyNameError = (value) => {
  const length = String(value || '').trim().length;
  if (length < STRATEGY_NAME_MINIMUM_LENGTH) {
    return `Enter a strategy name with at least ${STRATEGY_NAME_MINIMUM_LENGTH} characters.`;
  }
  if (length > STRATEGY_NAME_MAXIMUM_LENGTH) {
    return `Strategy names can be up to ${STRATEGY_NAME_MAXIMUM_LENGTH} characters.`;
  }
  return '';
};

export const guidedEntryAskHandoff = (conditions) => ({
  allowed: Array.isArray(conditions) && conditions.length === 0,
  currentDraft: null,
});

export const shouldShowOuterStrategySubmit = ({
  strategyBuildPath,
  editingStrategyId = null,
  hasDerivationSource = false,
}) => Boolean(
  editingStrategyId
  || hasDerivationSource
  || strategyBuildPath === 'advanced'
);

export const describeGuidedCondition = (condition) => {
  const presentation = CONDITION_PRESENTATION[condition?.capability_id];
  if (!presentation) return '';
  const value = presentation.parameter === 'rsi_threshold'
    ? condition.decimal_parameter
    : condition.integer_parameter;
  if (presentation.parameter === 'consecutive_sessions' || presentation.parameter === 'indicator_window') {
    return `${presentation.label} — ${value} sessions`;
  }
  if (presentation.parameter === 'rsi_threshold') return `${presentation.label} — ${value}`;
  return presentation.label;
};

export const entryRemovalState = (conditions, index) => {
  const entryConditions = conditions.filter((_, candidate) => candidate !== index);
  return { entryConditions, addingCondition: entryConditions.length === 0 };
};

export const emptyGuidedState = () => ({
  strategyName: 'My strategy',
  entryConditions: [],
  entryCombination: 'ALL',
  takeProfitMode: '',
  takeProfitPercentage: '',
  technicalExitCondition: null,
  stopLossMode: '',
  stopLossPercentage: '',
  maximumHoldingEnabled: null,
  maximumHoldingSessions: '',
  sentimentEnabled: false,
  sentimentMinimumScore: '0',
});

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const validStoredCondition = (condition) => {
  if (!isPlainObject(condition)) return false;
  if (Object.keys(condition).sort().join(',') !== 'capability_id,decimal_parameter,integer_parameter') return false;
  if (!CONDITION_PRESENTATION[condition.capability_id]) return false;
  if (condition.integer_parameter !== null && !Number.isInteger(condition.integer_parameter)) return false;
  return condition.decimal_parameter === null || typeof condition.decimal_parameter === 'string';
};

export const readGuidedBrowserDraft = (raw) => {
  let candidate;
  try { candidate = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
  if (!isPlainObject(candidate) || candidate.contract !== GUIDED_DRAFT_CONTRACT) return null;
  if (!Number.isInteger(candidate.step) || candidate.step < 0 || candidate.step > 5) return null;
  const state = candidate.state;
  const expectedKeys = Object.keys(emptyGuidedState()).sort();
  if (!isPlainObject(state) || Object.keys(state).sort().join(',') !== expectedKeys.join(',')) return null;
  if (typeof state.strategyName !== 'string' || !Array.isArray(state.entryConditions)
    || !state.entryConditions.every(validStoredCondition)) return null;
  if (!['ALL', 'ANY'].includes(state.entryCombination)) return null;
  if (!['', 'percentage', 'technical', 'none'].includes(state.takeProfitMode)) return null;
  if (!['', 'percentage', 'disabled'].includes(state.stopLossMode)) return null;
  if (![null, true, false].includes(state.maximumHoldingEnabled)) return null;
  if (typeof state.sentimentEnabled !== 'boolean') return null;
  for (const key of ['takeProfitPercentage', 'stopLossPercentage', 'maximumHoldingSessions', 'sentimentMinimumScore']) {
    if (typeof state[key] !== 'string') return null;
  }
  if (state.technicalExitCondition !== null && !validStoredCondition(state.technicalExitCondition)) return null;
  return {
    step: candidate.step,
    state: {
      ...state,
      entryConditions: state.entryConditions.map((condition) => ({ ...condition })),
      technicalExitCondition: state.technicalExitCondition ? { ...state.technicalExitCondition } : null,
    },
  };
};

const conditionFitsCatalog = (condition, catalog) => {
  if (!catalog?.entry_conditions?.includes(condition.capability_id)) return false;
  const presentation = CONDITION_PRESENTATION[condition.capability_id];
  if (!presentation?.parameter) {
    return condition.integer_parameter === null && condition.decimal_parameter === null;
  }
  const value = presentation.parameter === 'rsi_threshold'
    ? condition.decimal_parameter
    : condition.integer_parameter;
  const bound = catalog.bounds?.[presentation.parameter];
  return presentation.parameter === 'rsi_threshold'
    ? numberWithinCatalogBound(value, bound)
    : integerWithinCatalogBound(value, bound);
};

export const guidedStateFitsCatalog = (state, catalog) => {
  const entryBound = catalog?.bounds?.entry_conditions;
  if (!integerWithinCatalogBound(state.entryConditions.length || 1, entryBound)) return false;
  if (!state.entryConditions.every((condition) => conditionFitsCatalog(condition, catalog))) return false;
  if (state.technicalExitCondition && !conditionFitsCatalog(state.technicalExitCondition, catalog)) return false;
  if (state.takeProfitMode === 'percentage'
    && !numberWithinCatalogBound(state.takeProfitPercentage, catalog.bounds?.percentage)) return false;
  if (state.stopLossMode === 'percentage'
    && !numberWithinCatalogBound(state.stopLossPercentage, catalog.bounds?.percentage)) return false;
  if (state.maximumHoldingEnabled
    && !integerWithinCatalogBound(state.maximumHoldingSessions, catalog.bounds?.maximum_holding_sessions)) return false;
  return !state.sentimentEnabled
    || numberWithinCatalogBound(state.sentimentMinimumScore, catalog.bounds?.sentiment_score);
};

export const guidedStateFromIntent = (intent) => ({
  strategyName: intent?.strategy_name_suggestion || 'My strategy',
  entryConditions: intent?.entry?.conditions || [],
  entryCombination: intent?.entry?.combination || 'ALL',
  takeProfitMode: intent?.exit?.take_profit_percentage != null
    ? 'percentage'
    : intent?.exit?.technical_exit?.enabled ? 'technical' : 'none',
  takeProfitPercentage: intent?.exit?.take_profit_percentage || '',
  technicalExitCondition: intent?.exit?.technical_exit?.condition || null,
  stopLossMode: intent?.exit?.stop_loss_percentage != null ? 'percentage' : 'disabled',
  stopLossPercentage: intent?.exit?.stop_loss_percentage || '',
  maximumHoldingEnabled: intent?.exit?.maximum_holding_sessions != null,
  maximumHoldingSessions: intent?.exit?.maximum_holding_sessions || '',
  sentimentEnabled: Boolean(intent?.sentiment?.enabled),
  sentimentMinimumScore: intent?.sentiment?.minimum_entry_score ?? '0',
});

export const buildStrategyIntent = (state) => ({
  contract_version: INTENT_CONTRACT,
  status: 'READY',
  strategy_name_suggestion: String(state.strategyName || '').trim() || 'My strategy',
  entry: {
    combination: state.entryConditions.length > 1 ? state.entryCombination : 'ALL',
    conditions: state.entryConditions,
  },
  exit: {
    take_profit_percentage: state.takeProfitMode === 'percentage' ? String(state.takeProfitPercentage) : null,
    stop_loss_percentage: state.stopLossMode === 'percentage' ? String(state.stopLossPercentage) : null,
    technical_exit: state.takeProfitMode === 'technical'
      ? { enabled: true, condition: state.technicalExitCondition }
      : { enabled: false, condition: null },
    maximum_holding_sessions: state.maximumHoldingEnabled ? Number(state.maximumHoldingSessions) : null,
  },
  reentry: { capability_id: 'entry_rule_must_recur' },
  sentiment: state.sentimentEnabled
    ? { enabled: true, minimum_entry_score: String(state.sentimentMinimumScore) }
    : { enabled: false, minimum_entry_score: null },
  clarifications: [],
  unsupported_requests: [],
  stock_mentions: [],
});
